import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import { ChevronRight, Close, Straighten } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    ProductAttributeItem,
    ProductPomMeasurementSizeGroup,
} from '@/services/query/productPomMeasurement/productPomMeasurement.types';
import { HangerCardSizeInfoPanel, getSizeInfoSummary } from './HangerCardSizeInfoPanel';
import { useHangerCardSizeInfo } from './useHangerCardSizeInfo';
import './HangerCardSizeInfo.scss';

interface HangerCardSizeInfoPopoverProps {
    barcode: string;
    selectedSizeCode?: string;
    sizeMeasurementText?: string;
    sizeInfoSizes?: ProductPomMeasurementSizeGroup[];
    sizeInfoProductAttributes?: ProductAttributeItem[];
    sizeInfoLoaded?: boolean;
    onSizeSelectionChange: (sizeCode: string, measurementText: string) => void;
    onSizeInfoDataChange: (
        sizes: ProductPomMeasurementSizeGroup[],
        productAttributes: ProductAttributeItem[],
        loaded: boolean
    ) => void;
    onSizeMeasurementTextChange: (text: string) => void;
}

export const HangerCardSizeInfoPopover: FC<HangerCardSizeInfoPopoverProps> = ({
    barcode,
    selectedSizeCode,
    sizeMeasurementText,
    sizeInfoSizes = [],
    sizeInfoProductAttributes = [],
    sizeInfoLoaded = false,
    onSizeSelectionChange,
    onSizeInfoDataChange,
    onSizeMeasurementTextChange,
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const autoRestoreAttemptedRef = useRef(false);

    const {
        loading,
        error,
        fetched,
        sizes,
        productAttributes,
        fetchSizeInfo,
        handleSizeCodeChange,
        trimmedBarcode,
    } = useHangerCardSizeInfo({
        barcode,
        selectedSizeCode,
        sizes: sizeInfoSizes,
        productAttributes: sizeInfoProductAttributes,
        sizeInfoLoaded,
        onSizeSelectionChange,
        onSizeInfoDataChange,
    });

    useEffect(() => {
        if (!open) {
            autoRestoreAttemptedRef.current = false;
            return;
        }

        if (autoRestoreAttemptedRef.current || loading || !trimmedBarcode) {
            return;
        }

        const hasCachedSizes = sizes.length > 0;
        const hasPersistedSelection = Boolean(selectedSizeCode?.trim() || sizeMeasurementText?.trim());

        if (!hasCachedSizes && hasPersistedSelection) {
            autoRestoreAttemptedRef.current = true;
            void fetchSizeInfo();
        }
    }, [
        open,
        loading,
        trimmedBarcode,
        sizes.length,
        selectedSizeCode,
        sizeMeasurementText,
        fetchSizeInfo,
    ]);

    const summary = useMemo(() => {
        const summaryKey = getSizeInfoSummary(
            barcode,
            selectedSizeCode,
            sizeMeasurementText,
            sizeInfoLoaded,
            sizes,
            loading
        );

        if (summaryKey === 'ready') {
            return t('mannequin.aiGeneration.configuration.sizeInfo.summaryMeasurementsReady');
        }

        if (summaryKey) {
            return t('mannequin.aiGeneration.configuration.sizeInfo.summarySize', { size: summaryKey });
        }

        return null;
    }, [barcode, selectedSizeCode, sizeMeasurementText, sizeInfoLoaded, sizes, loading, t]);

    return (
        <>
            <button
                type="button"
                className="hanger-size-info-popover__trigger"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                aria-expanded={open}
                aria-haspopup="dialog"
            >
                <Straighten className="hanger-size-info-popover__trigger-icon" fontSize="small" />
                <Box className="hanger-size-info-popover__trigger-text">
                    <Typography variant="body2" className="hanger-size-info-popover__trigger-title">
                        {t('mannequin.aiGeneration.configuration.sizeInfo.title')}
                    </Typography>
                    {summary && (
                        <Typography variant="caption" className="hanger-size-info-popover__trigger-summary">
                            {summary}
                        </Typography>
                    )}
                </Box>
                <ChevronRight className="hanger-size-info-popover__trigger-chevron" fontSize="small" />
            </button>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        className: 'hanger-size-info-popover__paper',
                    },
                }}
            >
                <Box className="hanger-size-info-popover__header">
                    <Typography variant="subtitle2">
                        {t('mannequin.aiGeneration.configuration.sizeInfo.title')}
                    </Typography>
                    <IconButton size="small" onClick={() => setAnchorEl(null)} aria-label={t('general.close', 'Close')}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>

                <HangerCardSizeInfoPanel
                    variant="popover"
                    barcode={barcode}
                    selectedSizeCode={selectedSizeCode}
                    sizeMeasurementText={sizeMeasurementText}
                    loading={loading}
                    error={error}
                    fetched={fetched}
                    sizes={sizes}
                    productAttributes={productAttributes}
                    onFetch={() => void fetchSizeInfo()}
                    onSizeCodeChange={handleSizeCodeChange}
                    onSizeMeasurementTextChange={onSizeMeasurementTextChange}
                />
            </Popover>
        </>
    );
};
